import { Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../../../components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { cn } from '../../../lib/utils';

export type AIConfidence = 'high' | 'medium' | 'low';

export interface AISuggestedBadgeProps {
  confidence?: AIConfidence;
  label?: string;
  tooltip?: string;
  className?: string;
}

/**
 * Small badge indicating a value was AI-suggested.
 *
 * Visibility by confidence:
 * - high: renders nothing (silent — caller can trust the value)
 * - medium: neutral badge
 * - low: amber/warning tone (suggest user review)
 */
export function AISuggestedBadge({
  confidence = 'medium',
  label,
  tooltip,
  className,
}: AISuggestedBadgeProps) {
  const { t } = useTranslation('common');

  if (confidence === 'high') return null;

  const resolvedLabel = label ?? t('aiSuggested.label');
  const resolvedTooltip = tooltip ?? t('aiSuggested.tooltip');

  const variantClasses =
    confidence === 'low'
      ? 'bg-warning/10 text-warning border-warning/20 hover:bg-warning/15'
      : 'bg-primary/10 text-primary border-primary/20 hover:bg-primary/15';

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            aria-label={resolvedLabel}
            tabIndex={0}
            className={cn(
              'gap-1 font-medium cursor-help',
              variantClasses,
              className,
            )}
          >
            <Sparkles className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span>{resolvedLabel}</span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top" collisionPadding={16} className="max-w-[280px] text-xs">
          <p>{resolvedTooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
